import React, { useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { visuallyHidden } from "@mui/utils";
import {
  ABO_COLORS,
  tableBodyCellSx,
  tableHeadCellSx,
} from "../CampaignAbo/lib/campaignAboStyles";

const BODY_FONT_SIZE = "0.75rem";

const STATUS_COLORS = {
  SUCCEEDED: { bg: "#ECFDF5", color: "#047857", border: "#A7F3D0" },
  SKIPPED: { bg: "#FFF7ED", color: "#C2410C", border: "#FDBA74" },
  FAILED: { bg: "#FEF2F2", color: "#B91C1C", border: "#FECACA" },
};

const zIndexes = {
  header: 5,
  stickyColumn: 6,
};

const bodyTextSx = {
  fontSize: BODY_FONT_SIZE,
  fontWeight: 500,
  color: ABO_COLORS.body,
  lineHeight: 1.45,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

function buildColumnsWithStickyOffsets(columns = []) {
  let stickyOffset = 0;
  let lastStickyIndex = -1;

  columns.forEach((column, index) => {
    if (column.isSticky) lastStickyIndex = index;
  });

  return columns.map((column, index) => {
    if (!column.isSticky) {
      return { ...column, stickyLeft: 0, isLastSticky: false };
    }

    const withOffset = {
      ...column,
      stickyLeft: stickyOffset,
      isLastSticky: index === lastStickyIndex,
    };
    stickyOffset += column.width;
    return withOffset;
  });
}

const stickyHeaderCellSx = (column) => ({
  position: "sticky",
  left: column.stickyLeft,
  zIndex: zIndexes.stickyColumn,
  width: column.width,
  minWidth: column.width,
  maxWidth: column.width,
  boxSizing: "border-box",
  bgcolor: ABO_COLORS.tableHeadBg,
  color: ABO_COLORS.tableHeadText,
  fontWeight: 700,
  fontSize: "0.8125rem",
  whiteSpace: "nowrap",
  borderRight: column.isLastSticky
    ? `2px solid ${ABO_COLORS.border}`
    : `1px solid ${ABO_COLORS.border}`,
  py: 1.25,
  px: 1.5,
  overflow: "hidden",
});

const stickyBodyCellSx = (column, isActiveSort) => ({
  position: "sticky",
  left: column.stickyLeft,
  zIndex: 2,
  width: column.width,
  minWidth: column.width,
  maxWidth: column.width,
  boxSizing: "border-box",
  bgcolor: isActiveSort ? ABO_COLORS.tableHeadBg : ABO_COLORS.cardBg,
  borderRight: column.isLastSticky
    ? `2px solid ${ABO_COLORS.border}`
    : `1px solid ${ABO_COLORS.border}`,
  ...tableBodyCellSx,
  ...bodyTextSx,
  py: 1,
  px: 1.5,
});

const headerCellSx = (width) => ({
  ...tableHeadCellSx,
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box",
  bgcolor: ABO_COLORS.tableHeadBg,
  borderRight: `1px solid ${ABO_COLORS.border}`,
  px: 1.5,
  overflow: "hidden",
});

const bodyCellSx = (width, isActiveSort) => ({
  ...tableBodyCellSx,
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box",
  bgcolor: isActiveSort ? ABO_COLORS.tableHeadBg : ABO_COLORS.cardBg,
  borderRight: `1px solid ${ABO_COLORS.border}`,
  verticalAlign: "top",
  ...bodyTextSx,
  py: 1,
  px: 1.5,
});

export function formatChangeTypeDisplay(entityChange, taskType) {
  if (!entityChange) return "-";

  const formatted =
    String(entityChange).charAt(0).toUpperCase() + String(entityChange).slice(1);

  if (taskType) {
    const formattedTaskType = String(taskType).replace(/_/g, " ");
    return `${formatted} | ${formattedTaskType}`;
  }

  return formatted;
}

const WrappedText = ({ text }) => {
  const displayText = text ?? "-";

  return (
    <Typography
      component="span"
      sx={{
        display: "block",
        ...bodyTextSx,
      }}
    >
      {displayText}
    </Typography>
  );
};

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

export function stripMessageHtml(text) {
  if (text == null || text === "") return "";
  return String(text)
    .replace(/\\n/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?[^>]+>/g, "")
    .trim();
}

const MultilineText = ({ text }) => {
  if (text == null || text === "") {
    return <WrappedText text="-" />;
  }

  const displayText = String(text).replace(/\\n/g, "\n");

  if (HTML_TAG_PATTERN.test(displayText)) {
    return (
      <Box
        component="span"
        sx={{
          display: "block",
          ...bodyTextSx,
          whiteSpace: "pre-line",
          "& b, & strong": { fontWeight: 700 },
        }}
        dangerouslySetInnerHTML={{ __html: displayText }}
      />
    );
  }

  return (
    <Typography
      component="span"
      sx={{
        display: "block",
        ...bodyTextSx,
        whiteSpace: "pre-line",
      }}
    >
      {displayText}
    </Typography>
  );
};

const StatusCell = ({ status }) => {
  if (!status) return <WrappedText text="-" />;

  const normalizedStatus = String(status).toUpperCase();
  const palette = STATUS_COLORS[normalizedStatus] || {
    bg: "#F8FAFC",
    color: ABO_COLORS.body,
    border: ABO_COLORS.border,
  };

  return (
    <Chip
      label={normalizedStatus.replace(/_/g, " ")}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.6875rem",
        fontWeight: 600,
        bgcolor: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        textTransform: "capitalize",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const SKELETON_ROW_COUNT = 10;

const HistoryLogsTable = ({
  columns = [],
  rows = [],
  isLoading = false,
  orderBy = "created_at",
  order = "desc",
  onSort,
}) => {
  const resolvedColumns = useMemo(
    () => buildColumnsWithStickyOffsets(columns),
    [columns]
  );

  const tableMinWidth = useMemo(
    () => resolvedColumns.reduce((sum, column) => sum + column.width, 0),
    [resolvedColumns]
  );

  const handleSort = (column) => {
    if (!column?.sortable || !column?.id || !onSort) return;
    const isAsc = orderBy === column.id && order === "asc";
    onSort(column.id, isAsc ? "desc" : "asc");
  };

  const headerLabelSx = {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: ABO_COLORS.tableHeadText,
  };

  const renderHeaderLabel = (column, isActiveSort) => {
    if (!column.sortable) {
      return (
        <Typography component="span" noWrap sx={headerLabelSx}>
          {column.label}
        </Typography>
      );
    }

    return (
      <TableSortLabel
        active={isActiveSort}
        direction={isActiveSort ? order : "asc"}
        sx={{
          maxWidth: "100%",
          "& .MuiTableSortLabel-icon": {
            color: `${ABO_COLORS.tableHeadText} !important`,
          },
        }}
      >
        <Typography component="span" noWrap sx={headerLabelSx}>
          {column.label}
        </Typography>
        {isActiveSort && (
          <Box sx={visuallyHidden}>
            {order === "desc" ? "sorted descending" : "sorted ascending"}
          </Box>
        )}
      </TableSortLabel>
    );
  };

  const renderCellValue = (row, column) => {
    const value = row[column.id];

    if (column.id === "message") {
      const isError =
        Object.prototype.hasOwnProperty.call(row, "error_message") &&
        row.error_message !== null &&
        row.error_message !== "";

      if (isError) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <MultilineText text={value} />
            <Tooltip
              title={String(row.error_message || "").replace(/\\n/g, "\n")}
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                aria-label="View error details"
                sx={{
                  p: 0,
                  mt: 0.15,
                  flexShrink: 0,
                  color: "error.main",
                  "&:hover": { bgcolor: "transparent" },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
      return <MultilineText text={value} />;
    }

    if (column.id === "entity_change") {
      return (
        <WrappedText text={formatChangeTypeDisplay(value, row.task_type)} />
      );
    }

    if (column.id === "created_at") {
      return <WrappedText text={row.created_at_display ?? value} />;
    }

    if (column.id === "tracking_id") {
      const trackingId = value || row.tracking_id;
      if (!trackingId) return <WrappedText text="-" />;

      return (
        <Tooltip title={trackingId} arrow placement="top">
          <Box component="span">
            <WrappedText text={row.tracking_id_display ?? trackingId} />
          </Box>
        </Tooltip>
      );
    }

    if (column.id === "status") {
      return <StatusCell status={value} />;
    }

    return <WrappedText text={value} />;
  };

  if (!isLoading && rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 250,
          color: "text.secondary",
          gap: 1.5,
          border: `1px solid ${ABO_COLORS.border}`,
          borderRadius: "8px",
          marginTop: 2,
          bgcolor: ABO_COLORS.cardBg,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography variant="subtitle1">No Data Found</Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        overflow: "hidden",
        border: `1px solid ${ABO_COLORS.border}`,
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        marginTop: 2,
        bgcolor: ABO_COLORS.cardBg,
      }}
    >
      <TableContainer
        sx={{
          maxHeight: 600,
          minHeight: 400,
          overflow: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            width: tableMinWidth,
            minWidth: tableMinWidth,
            borderCollapse: "separate",
            borderSpacing: 0,
            "& .MuiTableCell-root": {
              boxSizing: "border-box",
            },
          }}
        >
          <colgroup>
            {resolvedColumns.map((column) => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
          </colgroup>

          <TableHead>
            <TableRow
              sx={{
                zIndex: zIndexes.header,
                position: "sticky",
                top: 0,
              }}
            >
              {resolvedColumns.map((column) => {
                const isSticky = column.isSticky;
                const isActiveSort =
                  column.sortable && orderBy === column.id;
                const headerSx = {
                  borderBottom: `1px solid ${ABO_COLORS.border}`,
                  cursor: column.sortable ? "pointer" : "default",
                };

                if (isSticky) {
                  return (
                    <TableCell
                      key={`${column.id}-header-sticky`}
                      align="left"
                      sx={{
                        ...stickyHeaderCellSx(column),
                        ...headerSx,
                      }}
                      onClick={() => handleSort(column)}
                    >
                      {renderHeaderLabel(column, isActiveSort)}
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={`${column.id}-header`}
                    align="left"
                    sx={{
                      ...headerCellSx(column.width),
                      ...headerSx,
                      zIndex: zIndexes.header,
                    }}
                    onClick={() => handleSort(column)}
                  >
                    {renderHeaderLabel(column, isActiveSort)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {resolvedColumns.map((column) => {
                      const isSticky = column.isSticky;
                      return (
                        <TableCell
                          key={`skeleton-${rowIndex}-${column.id}`}
                          align="left"
                          sx={
                            isSticky
                              ? stickyBodyCellSx(column, false)
                              : bodyCellSx(column.width, false)
                          }
                        >
                          <Skeleton variant="text" width="80%" height={18} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : rows.map((row, rowIndex) => (
                  <TableRow
                    key={row.id || `${row.created_at}-${rowIndex}`}
                    hover
                    sx={{
                      "&:hover td": {
                        bgcolor: "#F8FAFC",
                      },
                    }}
                  >
                    {resolvedColumns.map((column) => {
                      const isSticky = column.isSticky;
                      const isActiveSort =
                        column.sortable && orderBy === column.id;

                      return (
                        <TableCell
                          key={`${column.id}-${rowIndex}`}
                          align="left"
                          sx={
                            isSticky
                              ? stickyBodyCellSx(column, isActiveSort)
                              : bodyCellSx(column.width, isActiveSort)
                          }
                        >
                          {renderCellValue(row, column)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default HistoryLogsTable;
